import queryClient from '../../../ReactQuery/client';
import useToastedMutation from '../../../ReactQuery/useToastedMutation';
import { post } from '../../../redux/api';
import type { RetryFossabotPRAnalysisRequest } from '../../../../shared/fossabot/request';
import type { RetryFossabotPRAnalysisResponse } from '../../../../shared/fossabot/response';
import type { ErrorResponse } from '../../../ReactQuery/errors';

import { getFossabotIssueQueryKey } from './useFossabotPRStatus';

type RetryFossabotPRAnalysisVariables = RetryFossabotPRAnalysisRequest & {
  /** The FOSSA issue whose open PR should be re-analyzed. */
  issueId: number;
};

const retryFossabotPRAnalysis = async ({ issueId, ...body }: RetryFossabotPRAnalysisVariables) => {
  const response = await post<RetryFossabotPRAnalysisRequest, RetryFossabotPRAnalysisResponse>(
    `/api/fossabot/issues/${issueId}/dependency-upgrade-pr/retry`,
    body,
  );
  if (!response.data) {
    throw new Error('No data returned from the server');
  }
  return response.data;
};

/**
 * Asks fossabot to re-run the analysis on an issue's open PR, for the box's
 * "analysis delayed" state. Success has no toast: the box dropping back to the
 * plain 'analyzing' state (via the invalidated status query) is the feedback.
 */
export function useRetryFossabotPRAnalysis() {
  return useToastedMutation<RetryFossabotPRAnalysisResponse, ErrorResponse, RetryFossabotPRAnalysisVariables>({
    mutationFn: retryFossabotPRAnalysis,
    errorToastMessage: 'Failed to retry the analysis.',
    onSuccess: (_data, { issueId }) => {
      queryClient.invalidateQueries({ queryKey: getFossabotIssueQueryKey(issueId) });
    },
  });
}
